import React from 'react';
import type {GridRenderCellParams} from '@mui/x-data-grid-pro';
import assert from 'assert-ts';
import type {LocalizationContextType} from 'localization/context/types';
import type {Part} from 'schemaDefinition/types';
import type {CodeListMap} from 'services/codelists/types';
import type {ThesaurusesMap} from 'services/thesaurus/types';
import {Layout} from 'components/layout';
import {TextMatch} from 'components/text';
import {
  formatCodeListValue,
  formatLinkedValueLink,
  formatNameVariantValue,
  formatScalarValue,
  formatThesaurusValue,
} from 'schema/preview/functions';

export const mapRenderCell = (
  part: Part,
  params: GridRenderCellParams,
  l: LocalizationContextType,
  c: CodeListMap,
  t: ThesaurusesMap,
  match?: RegExp,
  delimiter?: string,
): React.ReactNode => {
  assert(part.type);

  const values = Array.isArray(params.value) ? params.value : [params.value];

  switch (part.type) {
    case 'text':
    case 'bool':
    case 'date':
    case 'int':
    case 'year':
    case 'yearOrDate':
      return (
        <Layout>
          <TextMatch variant="body1" match={match}>
            {formatScalarValue(values, part, l, delimiter)}
          </TextMatch>
        </Layout>
      );
    case 'textarea':
      return (
        <Layout>
          {values.map((v, idx) => {
            return (
              <TextMatch key={idx} variant="body1" match={match}>
                {v}
              </TextMatch>
            );
          })}
        </Layout>
      );
    case 'linkedLiterary':
    // @todo Map linked literary!
    // eslint-disable-next-line no-fallthrough
    case 'linkedAgent':
      return (
        <Layout>
          {values.map((v, idx) => {
            const name = formatLinkedValueLink(v);
            return name ? (
              <TextMatch key={idx} variant="body1" match={match}>
                {name}
              </TextMatch>
            ) : null;
          })}
        </Layout>
      );
    case 'nameVariant':
      return (
        <Layout>
          {values.map((v, idx) => (
            <TextMatch key={idx} variant="body1" match={match}>
              {formatNameVariantValue(v, part.agentType, 'excludeId')}
            </TextMatch>
          ))}
        </Layout>
      );
    case 'codelist':
      return (
        <Layout>
          <TextMatch variant="body1" match={match}>
            {formatCodeListValue(params.value, part, c[part.codelistId])}
          </TextMatch>
        </Layout>
      );
    case 'thesaurus':
      return (
        <Layout>
          <TextMatch variant="body1" match={match}>
            {formatThesaurusValue(params.value, part, t[part.thesaurusId])}
          </TextMatch>
        </Layout>
      );
    default:
      assert(false, `mapRenderCell: Missing mapping for part: ${part.type}`, {
        part,
      });
  }
};
